/**
 * crewIdentity.js: one way to turn a work order's crew into a name and a
 * colour (IDI-472).
 *
 * The bugs this replaces (measured on tenant 1, 2026-10-14):
 *  - Edit keyed crew options by `value ?? id`. A crew made in Settings has no
 *    `value`, and getOptions('crews') hands back a name SLUG for it
 *    ("test_crew"), so a work order was saved with crewId "test_crew". That
 *    matches no crew, so the chip printed the raw value. Crews are keyed by
 *    their `id` here, always (create already stores crewId = c.id).
 *  - Every chip read the `wo.crewColor` SNAPSHOT. "Test Crew" is red in
 *    Settings and showed green on J-000028. The live Settings colour wins;
 *    the snapshot is used only when the crew is gone from Settings.
 *
 * `crews` is the raw Settings list (useSettingsList('crews').items), NOT
 * getOptions('crews'): getOptions rewrites values for picker merging.
 */
import { CREW_COLORS } from './crewColors';

const norm = (s) => String(s ?? '').trim().toLowerCase();
const slug = (s) => norm(s).replace(/\s+/g, '_');

/** Picker options for a crew field: value = the crew's id. */
export function crewPickerOptions(crews) {
  return (Array.isArray(crews) ? crews : [])
    .filter((c) => c && c.id != null && c.active !== false)
    .map((c) => ({
      value: String(c.id),
      label: String(c.name || c.label || c.id),
      color: c.color || c.swatch || CREW_COLORS[0],
    }));
}

/**
 * Find the Settings crew a work order points at. Matches, in order: the id;
 * a legacy `value`; the slug getOptions used to hand out ("test_crew", which
 * old saves carry); the stored crew name. Returns null when the crew is gone.
 */
function findCrew(wo, crews) {
  const list = Array.isArray(crews) ? crews : [];
  const id = wo?.crewId != null ? String(wo.crewId) : '';
  if (id) {
    const byId = list.find((c) => String(c?.id) === id || (c?.value != null && String(c.value) === id));
    if (byId) return byId;
    const bySlug = list.find((c) => slug(c?.name) === id);
    if (bySlug) return bySlug;
  }
  const name = norm(wo?.crewName || wo?.crew);
  if (name) {
    const byName = list.find((c) => norm(c?.name) === name);
    if (byName) return byName;
  }
  return null;
}

/**
 * What to show for a work order's crew.
 * @returns {{ crewId: string, name: string, color: string, resolved: boolean, assigned: boolean }}
 *   resolved: the crew exists in Settings now.
 *   assigned: the work order names a crew at all.
 */
export function crewIdentity(wo, crews) {
  const hit = findCrew(wo, crews);
  if (hit) {
    return {
      crewId: String(hit.id),
      name: String(hit.name || hit.label || hit.id),
      color: hit.color || hit.swatch || CREW_COLORS[0],
      resolved: true,
      assigned: true,
    };
  }
  // The crew is gone from Settings: show the stored snapshot, never an id.
  const storedName = String(wo?.crewName || wo?.crew || '').trim();
  const looksLikeId = (s) => /^[0-9]+$/.test(s) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s);
  const name = storedName && !looksLikeId(storedName) ? storedName : '';
  return {
    crewId: wo?.crewId != null ? String(wo.crewId) : '',
    name: name || (wo?.crewId || storedName ? 'Removed crew' : ''),
    color: wo?.crewColor || 'var(--neutral-500)',
    resolved: false,
    assigned: !!(wo?.crewId || storedName),
  };
}
